/**
 * ClassroomTools.jsx — Outils d'animation en salle pour le mode Projection.
 *
 * Deux outils indépendants :
 *   • <ClassroomTimer />  : horloge temps réel + compte à rebours 30/60/120 min
 *                          (pulse rouge quand < 2 min ; alerte finale)
 *   • <LaserPointer />    : pointeur laser rouge qui suit le curseur souris
 *
 * Utilisation dans PresentationMode :
 *   import { ClassroomTools } from '../../components/ClassroomTools';
 *   <ClassroomTools />
 *
 * Raccourcis clavier :
 *   L = toggle laser · T = toggle timer
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Clock, Play, Pause, RotateCcw, Target, X as XIcon, Pencil, Highlighter, Eraser } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   TIMER
   ═══════════════════════════════════════════════════════════════════════════ */

const PRESETS = [
  { label: '30 min', sec: 30 * 60 },
  { label: '60 min', sec: 60 * 60 },
  { label: '2 h',    sec: 120 * 60 },
];

function pad(n) { return String(n).padStart(2, '0'); }
function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
}

export function ClassroomTimer() {
  const [mode, setMode] = useState('clock'); // 'clock' | 'countdown'
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [now, setNow] = useState(new Date());
  const [expanded, setExpanded] = useState(true);
  const startRef = useRef(null);
  const initialRef = useRef(0);

  // Tick horloge (1s)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Tick countdown (250ms pour précision)
  useEffect(() => {
    if (mode !== 'countdown' || !running) return undefined;
    const id = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const r = initialRef.current - elapsed;
      if (r <= 0) {
        setRemaining(0);
        setRunning(false);
      } else {
        setRemaining(r);
      }
    }, 250);
    return () => clearInterval(id);
  }, [mode, running]);

  const startPreset = useCallback((seconds) => {
    setMode('countdown');
    setRemaining(seconds);
    initialRef.current = seconds;
    startRef.current = Date.now();
    setRunning(true);
  }, []);

  const toggleRun = useCallback(() => {
    if (mode !== 'countdown') return;
    if (running) {
      // pause : sauvegarde le remaining courant
      initialRef.current = remaining;
      setRunning(false);
    } else {
      startRef.current = Date.now();
      setRunning(true);
    }
  }, [mode, running, remaining]);

  const reset = useCallback(() => {
    setRunning(false);
    setMode('clock');
    setRemaining(0);
  }, []);

  const isWarn = mode === 'countdown' && remaining > 0 && remaining <= 120;
  const isDone = mode === 'countdown' && remaining === 0 && initialRef.current > 0 && !running;

  return (
    <div
      className="fixed top-4 left-4 z-[10001] select-none"
      data-testid="classroom-timer"
    >
      {expanded ? (
        <div
          className={`rounded-2xl border-2 backdrop-blur-md shadow-2xl transition-all ${
            isDone ? 'border-red-500 bg-red-950/90 animate-pulse' :
            isWarn ? 'border-orange-500 bg-orange-950/85' :
            'border-white/20 bg-black/70'
          }`}
        >
          <div className="flex items-center justify-between gap-2 px-3 pt-2">
            <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/70">
              <Clock className="w-3 h-3" /> Timer salle
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-white/50 hover:text-white/90 transition-colors"
              aria-label="Réduire le timer"
              data-testid="timer-collapse"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-4 pb-3 pt-1">
            {/* Affichage principal */}
            <div
              className={`font-black tabular-nums leading-none tracking-tight ${
                isDone ? 'text-red-300' : isWarn ? 'text-orange-300' : 'text-white'
              }`}
              style={{ fontFamily: 'Source Code Pro, ui-monospace, monospace', fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
              data-testid="timer-display"
            >
              {mode === 'clock'
                ? `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
                : fmt(remaining)}
            </div>

            <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/50">
              {mode === 'clock' ? 'Heure actuelle' : isDone ? '⏰ Temps écoulé !' : running ? 'En cours' : 'En pause'}
            </div>

            {/* Presets */}
            <div className="mt-3 flex items-center gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => startPreset(p.sec)}
                  className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/25 border border-white/15 text-white text-[10px] font-black uppercase tracking-wider transition-colors"
                  data-testid={`timer-preset-${p.sec}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Contrôles countdown */}
            {mode === 'countdown' && (
              <div className="mt-2 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={toggleRun}
                  disabled={isDone}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[10px] font-black uppercase tracking-wider transition-colors"
                  data-testid="timer-toggle"
                >
                  {running ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Reprendre</>}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/25 border border-white/15 text-white text-[10px] font-black uppercase tracking-wider transition-colors"
                  data-testid="timer-reset"
                >
                  <RotateCcw className="w-3 h-3" /> Horloge
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`px-3 py-2 rounded-full border-2 backdrop-blur-md text-white text-xs font-black uppercase tracking-widest transition-colors ${
            isWarn ? 'border-orange-500 bg-orange-950/90' :
            isDone ? 'border-red-500 bg-red-950/90 animate-pulse' :
            'border-white/20 bg-black/60 hover:bg-black/80'
          }`}
          data-testid="timer-expand"
          aria-label="Ouvrir le timer"
        >
          <Clock className="w-4 h-4 inline mr-1.5" />
          {mode === 'clock'
            ? `${pad(now.getHours())}:${pad(now.getMinutes())}`
            : fmt(remaining)}
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LASER POINTER
   ═══════════════════════════════════════════════════════════════════════════ */

export function LaserPointer() {
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: -100, y: -100 });
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef(null);

  // Toggle avec touche L
  useEffect(() => {
    const onKey = (e) => {
      // Ignore si focus dans un champ texte
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setActive((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Suivi souris + auto-hide après 3s d'inactivité
  useEffect(() => {
    if (!active) return undefined;
    const onMove = (e) => {
      setPos({ x: e.clientX, y: e.clientY });
      setVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setVisible(false), 3000);
    };
    window.addEventListener('mousemove', onMove);
    // masquer curseur natif quand actif
    document.body.style.cursor = 'none';
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.body.style.cursor = '';
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [active]);

  return (
    <>
      {/* Toggle button (bottom-right) */}
      <button
        type="button"
        onClick={() => setActive((v) => !v)}
        className={`fixed bottom-4 right-4 z-[10001] inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 backdrop-blur-md text-xs font-black uppercase tracking-widest transition-all ${
          active
            ? 'border-red-500 bg-red-950/90 text-red-300 shadow-[0_0_20px_rgba(239,68,68,0.5)]'
            : 'border-white/20 bg-black/60 text-white hover:bg-black/80'
        }`}
        data-testid="laser-toggle"
        title="Pointeur laser (touche L)"
      >
        <Target className="w-4 h-4" />
        Laser {active && '· ON'}
      </button>

      {/* Le pointeur laser */}
      {active && visible && (
        <div
          className="fixed pointer-events-none z-[10002]"
          style={{
            left: pos.x,
            top: pos.y,
            transform: 'translate(-50%, -50%)',
          }}
          data-testid="laser-pointer"
          aria-hidden="true"
        >
          {/* Halo externe pulse */}
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              width: 40, height: 40,
              transform: 'translate(-50%, -50%)',
              background: 'radial-gradient(circle, rgba(239,68,68,0.4) 0%, transparent 70%)',
            }}
          />
          {/* Cercle principal */}
          <div
            className="rounded-full"
            style={{
              width: 24, height: 24,
              transform: 'translate(-50%, -50%)',
              background: 'radial-gradient(circle, rgba(239,68,68,0.95) 0%, rgba(220,38,38,0.7) 40%, rgba(153,27,27,0.3) 80%, transparent 100%)',
              boxShadow: '0 0 24px 8px rgba(239,68,68,0.7), 0 0 8px 3px rgba(255,255,255,0.4) inset',
            }}
          />
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE ANNOTATOR — trait libre + surligneur sur la slide projetée
   ═══════════════════════════════════════════════════════════════════════════ */

const ANNOTATION_COLORS = [
  { key: 'red',    hex: '#ef4444', label: 'Rouge' },
  { key: 'yellow', hex: '#facc15', label: 'Jaune' },
  { key: 'cyan',   hex: '#22d3ee', label: 'Cyan' },
];

export function SlideAnnotator() {
  const [active, setActive] = useState(false);
  const [tool, setTool] = useState('pen'); // 'pen' | 'highlighter'
  const [color, setColor] = useState('#ef4444');
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  // Ajuste la taille du canvas au viewport (retina-aware)
  const resizeCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = window.innerWidth * dpr;
    c.height = window.innerHeight * dpr;
    c.style.width = `${window.innerWidth}px`;
    c.style.height = `${window.innerHeight}px`;
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const clearCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
  }, []);

  // Redim + init
  useEffect(() => {
    if (!active) return undefined;
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [active, resizeCanvas]);

  // Effacer auto quand on change de slide (touches nav)
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Toggle annotation (A)
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        setActive((v) => !v);
      }
      // Effacer (E)
      else if ((e.key === 'e' || e.key === 'E') && active) {
        e.preventDefault();
        clearCanvas();
      }
      // Auto-clear sur navigation slide
      else if (active && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageUp' || e.key === 'PageDown')) {
        clearCanvas();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, clearCanvas]);

  // Dessin
  const startDraw = (e) => {
    if (!active) return;
    drawingRef.current = true;
    const rect = canvasRef.current.getBoundingClientRect();
    lastPointRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const draw = (e) => {
    if (!drawingRef.current || !active) return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const rect = c.getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    ctx.strokeStyle = color;
    if (tool === 'highlighter') {
      ctx.globalAlpha = 0.4;
      ctx.lineWidth = 22;
      ctx.globalCompositeOperation = 'multiply';
    } else {
      ctx.globalAlpha = 1;
      ctx.lineWidth = 4;
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };
  const stopDraw = () => { drawingRef.current = false; };

  return (
    <>
      {/* Toggle button (bottom-left) */}
      <button
        type="button"
        onClick={() => setActive((v) => !v)}
        className={`fixed bottom-4 left-4 z-[10001] inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 backdrop-blur-md text-xs font-black uppercase tracking-widest transition-all ${
          active
            ? 'border-yellow-400 bg-yellow-950/90 text-yellow-200 shadow-[0_0_20px_rgba(250,204,21,0.5)]'
            : 'border-white/20 bg-black/60 text-white hover:bg-black/80'
        }`}
        data-testid="annotate-toggle"
        title="Annotation (touche A)"
      >
        <Pencil className="w-4 h-4" />
        Annoter {active && '· ON'}
      </button>

      {/* Toolbar quand actif */}
      {active && (
        <div
          className="fixed bottom-16 left-4 z-[10001] flex items-center gap-1.5 px-2.5 py-2 rounded-2xl border-2 border-white/20 bg-black/70 backdrop-blur-md"
          data-testid="annotate-toolbar"
        >
          {/* Outils */}
          <button
            type="button"
            onClick={() => setTool('pen')}
            className={`p-1.5 rounded-lg transition-colors ${tool === 'pen' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white/90'}`}
            title="Stylo (trait fin)"
            data-testid="annotate-tool-pen"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setTool('highlighter')}
            className={`p-1.5 rounded-lg transition-colors ${tool === 'highlighter' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white/90'}`}
            title="Surligneur"
            data-testid="annotate-tool-highlighter"
          >
            <Highlighter className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-white/20 mx-0.5" />
          {/* Couleurs */}
          {ANNOTATION_COLORS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setColor(c.hex)}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c.hex ? 'scale-110 border-white' : 'border-white/30 hover:scale-105'}`}
              style={{ background: c.hex }}
              title={c.label}
              data-testid={`annotate-color-${c.key}`}
              aria-label={c.label}
            />
          ))}
          <div className="w-px h-6 bg-white/20 mx-0.5" />
          {/* Effacer */}
          <button
            type="button"
            onClick={clearCanvas}
            className="p-1.5 rounded-lg text-white/60 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            title="Effacer tout (E)"
            data-testid="annotate-clear"
          >
            <Eraser className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Canvas de dessin */}
      {active && (
        <canvas
          ref={canvasRef}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          className="fixed inset-0 z-[10000] cursor-crosshair"
          data-testid="annotate-canvas"
          aria-label="Zone d'annotation"
        />
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMBINED
   ═══════════════════════════════════════════════════════════════════════════ */

export function ClassroomTools() {
  return (
    <>
      <ClassroomTimer />
      <LaserPointer />
      <SlideAnnotator />
    </>
  );
}

export default ClassroomTools;
